import { useApp } from '../context/AppContext';
import { X, Plus, Minus, Trash2 } from './Icons';

interface CartProps {
  isOpen: boolean;
  onClose: () => void;
  onCheckout: () => void;
}

export default function CartDrawer({ isOpen, onClose, onCheckout }: CartProps) {
  const { lang, theme, cart, updateCartQuantity, removeFromCart } = useApp();
  if (!isOpen) return null;
  const isDark = theme === 'dark';
  const subtotal = cart.reduce((sum, item) => sum + item.product.price * item.quantity, 0);

  return (
    <div className="fixed inset-0 z-[210] flex justify-end bg-black/60 backdrop-blur-md animate-fade-in">
      {/* Click outside backdrop to close */}
      <div className="absolute inset-0 cursor-pointer" onClick={onClose} />
      
      <div 
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        className={`relative w-full max-w-sm h-full glass-panel shadow-[0_0_50px_rgba(0,0,0,0.15)] p-6 flex flex-col justify-between z-10 animate-in slide-in-from-right duration-250`}
      >
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex items-center justify-between pb-4 border-b border-neutral-150/40 dark:border-white/5 mb-4">
            <div className="flex flex-col">
              <h2 className="text-base font-black text-neutral-900 dark:text-white">{lang === 'en' ? 'Your Shopping Bag' : 'আপনার শপিং ব্যাগ'}</h2>
              <span className="text-[10px] text-neutral-400 font-medium">
                {lang === 'en' 
                  ? `(${cart.length} product${cart.length !== 1 ? 's' : ''})` 
                  : `(${cart.length} টি প্রোডাক্ট)`}
              </span>
            </div>
            <button 
              id="close-cart-btn" 
              onClick={onClose} 
              className="p-2 cursor-pointer rounded-full bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:bg-primary hover:text-white dark:hover:bg-primary dark:hover:text-white transition-all border border-neutral-200/50 dark:border-white/10 shadow-sm"
              aria-label="Close cart"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          <div className="flex-1 overflow-y-auto space-y-3.5 pr-1 no-scrollbar">
            {cart.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 rounded-full bg-rose-50 dark:bg-neutral-800/50 flex items-center justify-center mb-4 text-primary">
                  <svg className="w-8 h-8 stroke-[1.5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                  </svg>
                </div>
                <p className="text-sm font-medium text-neutral-400">{lang === 'en' ? 'Bag is empty' : 'শপিং ব্যাগ খালি আছে'}</p>
              </div>
            ) : cart.map((item) => (
              <div 
                key={item.product.id} 
                className="flex gap-3.5 p-3.5 bg-neutral-50/50 dark:bg-neutral-900/40 rounded-2xl border border-neutral-200/30 dark:border-white/[0.04] shadow-[0_8px_16px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.01)] dark:shadow-[0_8px_16px_rgba(0,0,0,0.25)] hover:shadow-[0_12px_24px_-4px_rgba(0,0,0,0.06),0_4px_8px_rgba(0,0,0,0.02)] dark:hover:shadow-[0_12px_24px_-4px_rgba(0,0,0,0.4)] hover:-translate-y-0.5 transition-all duration-200"
              >
                <div className="relative shrink-0">
                  <img src={item.product.image} className="w-14 h-14 object-cover rounded-xl border border-neutral-100 dark:border-white/5 shadow-sm" referrerPolicy="no-referrer" loading="lazy" decoding="async" />
                </div>
                <div className="flex-1 min-w-0 flex flex-col justify-between">
                  <div>
                    <h4 className="text-[12px] font-bold line-clamp-1 text-neutral-800 dark:text-neutral-100">{lang === 'en' ? item.product.nameEn : item.product.nameBn}</h4>
                    {(item.product.colorVar || item.product.sizeVar) && (
                      <div className="flex gap-1.5 flex-wrap mt-1">
                        {item.product.colorVar && (
                          <span className="text-[9px] font-black uppercase tracking-wide px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/15">
                            {item.product.colorVar}
                          </span>
                        )}
                        {item.product.sizeVar && (
                          <span className="text-[9px] font-black uppercase tracking-wide px-1.5 py-0.5 rounded bg-indigo-500/10 text-indigo-650 dark:text-indigo-400 border border-indigo-500/15">
                            {item.product.sizeVar}
                          </span>
                        )}
                      </div>
                    )}
                    <p className="text-xs text-primary dark:text-rose-455 font-black mt-1">{item.product.price} ৳</p>
                  </div>
                  
                  <div className="flex items-center justify-between mt-2.5 pt-2 border-t border-neutral-100/30 dark:border-white/5">
                    <div className="flex items-center gap-2 bg-neutral-100/80 dark:bg-neutral-800/80 rounded-full p-0.5 border border-neutral-200/10 dark:border-white/5">
                      <button 
                        id={`qty-minus-${item.product.id}`} 
                        onClick={() => updateCartQuantity(item.product.id, item.quantity - 1)} 
                        className="p-1 rounded-full cursor-pointer hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors flex items-center justify-center text-neutral-650 dark:text-neutral-350"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="text-xs font-black min-w-4 text-center text-neutral-800 dark:text-neutral-100">{item.quantity}</span>
                      <button 
                        id={`qty-plus-${item.product.id}`} 
                        onClick={() => updateCartQuantity(item.product.id, item.quantity + 1)} 
                        className="p-1 rounded-full cursor-pointer hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors flex items-center justify-center text-neutral-650 dark:text-neutral-350"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                    <button 
                      id={`qty-remove-${item.product.id}`} 
                      onClick={() => removeFromCart(item.product.id)} 
                      className="p-1.5 rounded-full cursor-pointer text-neutral-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-all"
                      aria-label="Remove item"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="pt-4 pb-4 border-t border-neutral-150/40 dark:border-white/5 mt-4">
          <div className="flex justify-between items-center mb-4 px-1">
            <span className="text-sm font-black text-neutral-500 dark:text-neutral-400">{lang === 'en' ? 'Subtotal' : 'সাবটোটাল'}</span>
            <span className="text-lg font-black text-primary dark:text-rose-450">{subtotal} ৳</span>
          </div>
          <button 
            id="cart-checkout-btn" 
            disabled={cart.length === 0} 
            onClick={() => { onCheckout(); onClose(); }} 
            className="w-full py-3.5 rounded-full bg-primary hover:bg-primary/90 text-white font-black text-xs uppercase tracking-wider cursor-pointer transition-all disabled:opacity-50 shadow-[0_4px_15px_rgba(var(--theme-rgb),0.4)] hover:shadow-[0_4px_25px_rgba(var(--theme-rgb),0.6)] hover:scale-[1.01] active:scale-95 duration-150 flex items-center justify-center gap-2"
          >
            <span>{lang === 'en' ? 'Checkout Now' : 'অর্ডার করুন'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
